import { useEffect } from 'react';

/**
 * AuthInit Component
 * 
 * This component handles authentication initialization when the app starts.
 * It only runs when the user is at the root path (/) and validates the token
 * to redirect appropriately.
 */

// Utility function to read token from storage
const readTokenFromStorage = () => {
  // Try multiple common token keys
  const tokenKeys = ['token', 'authToken', 'access_token'];
  
  // Check both localStorage and sessionStorage
  const storages = [localStorage, sessionStorage];
  
  for (const storage of storages) {
    for (const key of tokenKeys) {
      const token = storage.getItem(key);
      if (token) {
        return token;
      }
    }
  }
  
  return null;
};

// Utility function to decode JWT payload
const decodeJwtPayload = (token) => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// Utility function to validate JWT token
const isJwtValid = (token) => {
  if (!token) {
    return false;
  }
  
  try {
    const payload = decodeJwtPayload(token);
    
    if (!payload) {
      return false;
    }
    
    // Check if token has exp claim
    if (payload.exp) {
      // exp is in seconds (UNIX timestamp)
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      return expirationTime > currentTime;
    }
    
    // If no exp claim, check expiration from storage
    const expiration = localStorage.getItem('expiration') || sessionStorage.getItem('expiration');
    if (expiration) {
      const expDate = new Date(expiration).getTime();
      return expDate > Date.now();
    }
    
    // If no expiration info, consider token invalid for safety
    return false;
  } catch (error) {
    console.error('Error validating JWT:', error);
    return false;
  }
};

// Utility function to clear all auth tokens
const clearAuthTokens = () => {
  const tokenKeys = ['token', 'authToken', 'access_token', 'expiration', 'userData'];
  
  tokenKeys.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  
  // Las preferencias de "Recuérdame" no forman parte de la sesión y se conservan.
};

const AuthInit = () => {
  useEffect(() => {
    // Only execute this logic when the user is at the root path
    if (window.location.pathname !== '/') {
      return;
    }
    
    try {
      // Read token from storage
      const token = readTokenFromStorage();
      
      if (!token) {
        // No token found, redirect to login
        clearAuthTokens();
        window.location.replace('/login');
        return;
      }
      
      // Validate token
      if (isJwtValid(token)) {
        // Token is valid, redirect to sesiones
        window.location.replace('/sesiones');
      } else {
        // Token is invalid or expired, clear and redirect to login
        clearAuthTokens();
        window.location.replace('/login');
      }
    } catch (error) {
      // In case of any error, clear tokens and redirect to login
      console.error('Error during auth initialization:', error);
      clearAuthTokens();
      window.location.replace('/login');
    }
  }, []);
  
  // This component doesn't render anything
  return null;
};

export default AuthInit;
